// Lesson runtime scene. The canvas-mode sibling of GameScene: it reuses the same
// standalone plumbing (RafLoop, renderScene, InputHandler, ActionBar) but swaps
// who owns state mutation — every local action routes through a LessonController,
// which gates it against the current step and blocks wrong moves, instead of
// GameScene's BotDriver + raw GameState transitions. Bots settle synchronously
// inside the controller, so there is no timer loop here. The CoachPanel is
// deliberately absent — lessons carry their own authored instruction copy on the
// LessonCard.

import { themeToRenderPalette } from '../render/palette.js';
import { renderScene } from './SceneRender.js';
import { RafLoop } from './RafLoop.js';
import { resolveDiscardFromTap } from './SceneCallbacks.js';
import { applyLessonClaim, applyLessonDiscard } from './lessonActions.js';
import { LOCAL_SEAT } from './Bootstrap.js';
import { THEMES } from '../settings/themes.js';
import { TableViewport } from './TableViewport.js';
import { ActionBar } from '../ui/ActionBar.js';
import { LessonCard, type LessonCardView } from '../ui/LessonCard.js';
import { canTsumo, claimsFor } from '../game/GameState.js';
import type { Claim } from '../game/Claims.js';
import type { LessonController } from '../lessons/LessonController.js';
import type { LayoutManager } from '../layout/LayoutManager.js';
import type { SettingsManager } from '../settings/SettingsManager.js';

/** Lessons run on the Simplified ruleset, which never offers kong. */
function noop(): void {}

export interface LessonSceneOptions {
  readonly canvas: HTMLCanvasElement;
  readonly layoutManager: LayoutManager;
  readonly settingsManager: SettingsManager;
  /** The invite pill's bottom edge in canvas space, or null while it isn't showing. */
  readonly pillBottom?: () => number | null;
  /** The on-canvas pause gear was tapped (same as Escape). */
  readonly onPause: () => void;
}

export class LessonScene {
  private controller: LessonController | null = null;
  private onExit: (() => void) | null = null;
  private isDirty = true;
  private tableViewport: TableViewport | null = null;
  private readonly raf = new RafLoop(
    () => this.drawFrame(),
    () => this.markDirty(),
  );
  private readonly actionBar = new ActionBar();
  private readonly card = new LessonCard({
    onNext: () => this.handleNext(),
    onSkip: () => this.exit(),
    onExit: () => this.exit(),
  });

  constructor(private readonly opts: LessonSceneOptions) {}

  /** Take the canvas for `controller`'s lesson; `onExit` returns to the picker. */
  mount(controller: LessonController, onExit: () => void): void {
    this.controller = controller;
    this.onExit = onExit;
    this.isDirty = true;
    this.raf.start();
    this.tableViewport = new TableViewport({
      canvas: this.opts.canvas,
      layoutManager: this.opts.layoutManager,
      onTap: (x, y) => this.handleTap(x, y),
      onDirty: () => this.markDirty(),
      getState: () => this.controller?.getState() ?? null,
      getLocalSeat: () => LOCAL_SEAT,
      onPause: this.opts.onPause,
    });
    this.refresh();
  }

  unmount(): void {
    this.raf.stop();
    this.tableViewport?.dispose();
    this.tableViewport = null;
    this.actionBar.clear();
    this.card.clear();
    this.controller = null;
    this.onExit = null;
  }

  markDirty(): void {
    this.isDirty = true;
  }

  private exit(): void {
    this.onExit?.();
  }

  private handleNext(): void {
    const controller = this.controller;
    if (controller === null) return;
    if (controller.isComplete()) {
      this.exit();
      return;
    }
    controller.advance();
    this.refresh();
  }

  private handleTap(x: number, y: number): void {
    const controller = this.controller;
    if (controller === null) return;
    const layout = this.opts.layoutManager.getConfig();
    const tileId = resolveDiscardFromTap(controller.getState(), layout, x, y, LOCAL_SEAT);
    if (tileId === null) return;
    applyLessonDiscard(controller, LOCAL_SEAT, tileId);
    this.refresh();
  }

  private resolveClaim(choice: Claim | null): void {
    const controller = this.controller;
    if (controller === null) return;
    applyLessonClaim(controller, choice);
    this.refresh();
  }

  /** Re-derive both DOM surfaces + queue a repaint after any state change. */
  private refresh(): void {
    if (this.controller === null) return;
    this.card.update(this.cardView(this.controller));
    this.syncActionBar(this.controller);
    this.isDirty = true;
  }

  private cardView(controller: LessonController): LessonCardView {
    const progress = controller.progress();
    const step = controller.currentStep();
    return {
      instructionKey: step.instructionKey,
      instructionParams: step.instructionParams,
      stepIndex: progress.stepIndex,
      total: controller.stepCount(),
      canAdvance: controller.canAdvance(),
      isComplete: controller.isComplete(),
      rejectionKey: progress.rejectionKey,
    };
  }

  /** Show a Tsumo offer or claim buttons only when the current step calls for
   *  them; every choice is routed back through the gating controller. */
  private syncActionBar(controller: LessonController): void {
    this.actionBar.clear();
    if (controller.isComplete()) return;
    const state = controller.getState();
    const expected = controller.currentStep().expectedAction;
    if (
      state.phase === 'awaiting-discard' &&
      state.turn === LOCAL_SEAT &&
      expected.kind === 'tsumo' &&
      canTsumo(state)
    ) {
      this.actionBar.showOwnTurnOptions(true, [], () => this.resolveTsumo(), noop);
      return;
    }
    if (state.phase === 'claim-window') {
      const claims = claimsFor(state).find((c) => c.seat === LOCAL_SEAT);
      if (claims !== undefined) {
        this.actionBar.showClaimWindow(LOCAL_SEAT, claims, (choice) => this.resolveClaim(choice), noop);
      }
    }
  }

  private resolveTsumo(): void {
    const controller = this.controller;
    if (controller === null) return;
    controller.attemptTsumo(LOCAL_SEAT);
    this.refresh();
  }

  private drawFrame(): void {
    const controller = this.controller;
    if (controller === null || !this.isDirty || this.tableViewport === null) return;
    this.isDirty = false;
    const ctx = this.opts.canvas.getContext('2d');
    if (ctx === null) throw new Error('LessonScene: canvas 2D context unavailable');
    const settings = this.opts.settingsManager.get();
    const palette = themeToRenderPalette(THEMES[settings.theme]);
    const layout = this.opts.layoutManager.getConfig();
    const step = controller.currentStep();
    renderScene(
      ctx,
      controller.getState(),
      layout,
      palette,
      this.tableViewport.viewport,
      null,
      LOCAL_SEAT,
      this.opts.pillBottom?.() ?? null,
      step.highlightTileIds,
    );
    this.tableViewport.drawControls(ctx, palette);
  }
}
